/**
 * In-place editors for workspace-level cells in the nested saved views
 * (#1144, QA round 7): Position is a select, custom fields edit inline via
 * the existing CustomFieldInput (dropdowns for select/multi_select, typed
 * inputs otherwise), and Notes gains an add button. Shared by the nested
 * table and card renderings.
 *
 * Cells are editable only when the page supplies handlers — the server
 * still enforces workspace write permission; a rejected save surfaces as
 * the handler's toast, and the cell reverts.
 */

import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import MultiSelectDropdown from '../MultiSelectDropdown';
import type { CustomFieldDefinition } from '../../types/custom-fields';
import type { Bill, } from '../../types/bill';
import { BILL_POSITIONS } from '../../types/bill';
import type { BillWorkspaceRecord } from '../../types/savedViews';
import { getPositionColorClasses, getPositionLabel } from '../../utils/positionColors';

export interface RecordEditHandlers {
  onPositionChange: (bill: Bill, record: BillWorkspaceRecord, position: string) => Promise<void> | void;
  onCustomFieldSave: (
    bill: Bill,
    record: BillWorkspaceRecord,
    fieldDefinitionId: string,
    value: unknown
  ) => Promise<void> | void;
  onAddNote: (bill: Bill, record: BillWorkspaceRecord) => void;
}

export interface RecordEditing {
  handlers: RecordEditHandlers;
  /** Full definitions by id — CustomFieldInput needs field_type + field_config. */
  defsById: Record<string, CustomFieldDefinition>;
}

/** Position select styled as the pill it replaces. */
export const PositionEditCell: React.FC<{
  bill: Bill;
  record: BillWorkspaceRecord;
  onChange: RecordEditHandlers['onPositionChange'];
}> = ({ bill, record, onChange }) => {
  const position = record.position || 'Pending';
  return (
    <select
      value={position}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        onChange(bill, record, e.target.value);
      }}
      aria-label={`Position in ${record.workspaceName}`}
      className={`rounded-full border-0 px-2.5 py-0.5 pr-6 text-[11px] font-semibold cursor-pointer ${getPositionColorClasses(position)}`}
    >
      {BILL_POSITIONS.map((p) => (
        <option key={p.value} value={p.value}>
          {getPositionLabel(p.value)}
        </option>
      ))}
    </select>
  );
};

/**
 * Inline custom-field editor, formatted like the Position pill (QA round 8) —
 * no field label or description in the cell (the column header already names
 * it), no form-sized controls:
 *
 * - select / boolean / date: compact pill controls, saving on change
 * - multi_select: MultiSelectDropdown in its compact (pill) variant
 * - text / number / currency: a slim inline input, saving on blur or Enter
 * - richtext: read-only here — its markup needs the full editor, which is a
 *   form control, not a cell
 */
const PILL_SELECT =
  'rounded-full border border-border-light-secondary dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary px-2.5 py-0.5 pr-6 text-[11px] font-semibold text-text-secondary dark:text-text-dark-secondary cursor-pointer max-w-full';
const SLIM_INPUT =
  'h-6 w-full max-w-[170px] rounded-full border border-border-light-secondary dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary px-2.5 text-[11px] font-semibold text-text-secondary dark:text-text-dark-secondary focus:outline-none focus:border-primary-400';

export const CustomFieldEditCell: React.FC<{
  bill: Bill;
  record: BillWorkspaceRecord;
  field: CustomFieldDefinition;
  onSave: RecordEditHandlers['onCustomFieldSave'];
}> = ({ bill, record, field, onSave }) => {
  const stored = record.customFieldValues?.[field.id];
  const [draft, setDraft] = useState<unknown>(stored ?? null);
  useEffect(() => {
    setDraft(stored ?? null);
  }, [stored]);

  const commit = (value: unknown) => {
    const changed = JSON.stringify(value ?? null) !== JSON.stringify(stored ?? null);
    if (changed) onSave(bill, record, field.id, value);
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const options = field.field_config?.options ?? [];

  switch (field.field_type) {
    case 'select':
      return (
        <select
          value={typeof draft === 'string' ? draft : ''}
          onClick={stop}
          onChange={(e) => {
            stop(e);
            setDraft(e.target.value || null);
            commit(e.target.value || null);
          }}
          aria-label={`${field.name} in ${record.workspaceName}`}
          className={PILL_SELECT}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );

    case 'multi_select': {
      const selected = Array.isArray(draft) ? (draft as string[]) : [];
      return (
        <span onClick={stop} className="block min-w-0">
          <MultiSelectDropdown
            compact
            options={options.map((o) => ({ value: o, label: o }))}
            selected={selected}
            onChange={(next) => {
              setDraft(next);
              commit(next);
            }}
            allLabel="—"
            selectedWord="selected"
            ariaLabel={`${field.name} in ${record.workspaceName}`}
          />
        </span>
      );
    }

    case 'boolean':
      return (
        <select
          value={draft === true ? 'yes' : draft === false ? 'no' : ''}
          onClick={stop}
          onChange={(e) => {
            stop(e);
            const v = e.target.value === '' ? null : e.target.value === 'yes';
            setDraft(v);
            commit(v);
          }}
          aria-label={`${field.name} in ${record.workspaceName}`}
          className={PILL_SELECT}
        >
          <option value="">—</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );

    case 'date':
      return (
        <input
          type="date"
          value={typeof draft === 'string' ? draft : ''}
          onClick={stop}
          onChange={(e) => {
            stop(e);
            setDraft(e.target.value || null);
            commit(e.target.value || null);
          }}
          aria-label={`${field.name} in ${record.workspaceName}`}
          className={SLIM_INPUT}
        />
      );

    case 'richtext':
      // Markup needs the full editor; the cell stays read-only.
      return (
        <span className="block break-words text-[13px]">
          {typeof stored === 'string' && stored ? stored : '—'}
        </span>
      );

    default: {
      // text / number / currency: slim input, commit on blur or Enter.
      const isNumber = field.field_type === 'number' || field.field_type === 'currency';
      return (
        <input
          type={isNumber ? 'number' : 'text'}
          value={draft === null || draft === undefined ? '' : String(draft)}
          onClick={stop}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw === '' ? null : isNumber ? Number(raw) : raw);
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(draft);
          }}
          placeholder="—"
          aria-label={`${field.name} in ${record.workspaceName}`}
          className={SLIM_INPUT}
        />
      );
    }
  }
};

/** Notes summary (unchanged click-through) plus the add-note affordance. */
export const NotesEditCell: React.FC<{
  bill: Bill;
  record: BillWorkspaceRecord;
  summary: React.ReactNode;
  onAddNote: RecordEditHandlers['onAddNote'];
}> = ({ bill, record, summary, onAddNote }) => (
  <span className="flex items-center gap-1.5 min-w-0">
    <span className="min-w-0">{summary}</span>
    <button
      type="button"
      title={`Add note in ${record.workspaceName}`}
      aria-label={`Add note for ${bill.billNumber} in ${record.workspaceName}`}
      onClick={(e) => {
        e.stopPropagation();
        onAddNote(bill, record);
      }}
      className="flex h-5 w-5 flex-none items-center justify-center rounded-md border border-border-light-secondary dark:border-border-dark text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-700 hover:border-primary-300 dark:hover:text-primary-300 transition-colors"
    >
      <Plus className="w-3.5 h-3.5" />
    </button>
  </span>
);
